import {
  LayoutDashboard,
  Users,
  Phone,
  Activity,
  FileText,
  Settings,
  Network,
  Terminal,
  Users2,
  Clock,
  MessageSquare,
  MessageCircle,
  Hash,
  UsersRound,
  BarChart3,
  Shield,
  Calendar,
  MonitorPlay,
  ClipboardList,
  Gauge,
  Brain,
  Bell,
  Send,
  TrendingUp,
  CreditCard,
  HelpCircle,
  Target,
  Mail,
  Video,
  FolderOpen,
  ShoppingCart,
  Package,
  BookOpen as BookOpenIcon,
  Receipt,
  Wallet,
  Calculator,
  Layers,
  Settings2,
  Rss,
  Globe,
  GitBranch,
  Boxes,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import { getMasterPublicBaseUrl, WAREHOUSE_AREA_PERMISSIONS } from '@easyflow/shared';

export interface NavChild {
  name: string;
  href: string;
  icon: LucideIcon;
  level: number;
  newWindow?: boolean;
  adminOnly?: boolean;
  permission?: string;
}

export interface NavItem {
  name: string;
  href?: string;
  icon: LucideIcon;
  level: number;
  moduleKey?: string;
  permission?: string;
  /** Visibile se l'utente ha ALMENO UNO di questi permessi (alternativo a `permission`). */
  anyPermission?: readonly string[];
  exact?: boolean;
  children?: NavChild[];
  /** When true, admins see this entry even if the module is disabled or they
   *  lack the permission. Used for system tools like Company Wiki where the
   *  admin must always be able to manage/configure the module. */
  adminAlways?: boolean;
}

// Task #3585 — la console master vive sull'origin master dedicato
// (MASTER_PUBLIC_BASE_URL, default master.<brand>): link assoluti cross-origin,
// gestiti come <a> esterni da Sidebar/MobileSidebar.
const masterHref = (path: string): string => `${getMasterPublicBaseUrl()}${path}`;

export const getMasterNavigation = (): NavItem[] => [
  { name: 'Dashboard', href: masterHref('/dashboard'), icon: LayoutDashboard, level: 0 },
  { name: 'Security', href: masterHref('/security'), icon: Shield, level: 0 },
  { name: 'SIP Cluster', href: masterHref('/sip-cluster'), icon: Network, level: 0 },
  { name: 'Tenants', href: masterHref('/tenants'), icon: Users, level: 0 },
  { name: 'Users', href: masterHref('/users'), icon: Users, level: 0 },
  { name: 'Analytics', href: masterHref('/analytics'), icon: BarChart3, level: 0 },
];

export const getTenantNavigation = (tenantSlug: string): NavItem[] => [
  { name: 'Dashboard', href: `/${tenantSlug}/dashboard`, icon: LayoutDashboard, level: 0 },
  { name: 'Gestione Organizzazione', href: `/${tenantSlug}/tenant-management`, icon: Users, level: 0, permission: 'settings.manage' },
  {
    name: 'Gestione VoIP',
    icon: Phone,
    level: 0,
    moduleKey: 'pbx',
    children: [
      { name: 'Gestione PBX', href: `/${tenantSlug}/pbx`, icon: Settings, level: 1, permission: 'pbx.manage' },
      { name: 'Analytics', href: `/${tenantSlug}/analytics`, icon: BarChart3, level: 1, permission: 'analytics.view' },
      { name: 'Queue Dashboard', href: `/${tenantSlug}/queue-dashboard`, icon: Gauge, level: 1, permission: 'queues.view' },
      { name: 'Switchboard', href: `/${tenantSlug}/switchboard`, icon: MonitorPlay, level: 1, permission: 'switchboard.view' },
      { name: 'Live Calls', href: `/${tenantSlug}/live-calls`, icon: Activity, level: 1, permission: 'live_calls.view' },
    ]
  },
  { name: 'CDR', href: `/${tenantSlug}/cdr`, icon: FileText, level: 0, moduleKey: 'pbx', permission: 'cdr.view' },
  { name: 'Sveglie', href: `/${tenantSlug}/wakeup-calls`, icon: Bell, level: 0, moduleKey: 'pbx', permission: 'wakeup_calls.manage' },
  { name: 'Dashboard AI', href: `/${tenantSlug}/ai-tools`, icon: Brain, level: 0, moduleKey: 'ai_tools', permission: 'ai_tools.access' },
  { name: 'Company Wiki', href: `/${tenantSlug}/company-wiki`, icon: BookOpenIcon, level: 0, moduleKey: 'company_wiki', permission: 'company_wiki.access', adminAlways: true },
  { name: 'Web Builder', href: `/${tenantSlug}/web-builder`, icon: Globe, level: 0, moduleKey: 'web_builder', permission: 'site_builder.access' },
  { name: 'CRM', href: `/${tenantSlug}/crm`, icon: Target, level: 0, moduleKey: 'crm', permission: 'crm.view' },
  { name: 'To Do', href: `/${tenantSlug}/tasks`, icon: ClipboardList, level: 0, permission: 'tasks.view' },
  { name: 'HR', href: `/${tenantSlug}/hr`, icon: Users2, level: 0, moduleKey: 'hr', permission: 'hr.view' },
  { name: 'Video Call', href: `/${tenantSlug}/video-calls`, icon: Video, level: 0, permission: 'video_call.access' },
  { name: 'Calendario', href: `/${tenantSlug}/calendar`, icon: Calendar, level: 0, moduleKey: 'calendar', permission: 'calendar.view' },
  {
    name: 'Comunicazione',
    icon: MessageCircle,
    level: 0,
    moduleKey: 'email',
    permission: 'messenger.access',
    children: [
      { name: 'Chat', href: `/${tenantSlug}/messaging/chat`, icon: MessageSquare, level: 1 },
      { name: 'Email', href: `/${tenantSlug}/messaging/email`, icon: Mail, level: 1, permission: 'email.view' },
      { name: 'Analytics', href: `/${tenantSlug}/messaging/analytics`, icon: TrendingUp, level: 1, permission: 'analytics.view' },
      { name: 'Billing', href: `/${tenantSlug}/messaging/billing`, icon: CreditCard, level: 1, permission: 'settings.manage' },
      { name: 'Impostazioni', href: `/${tenantSlug}/messaging/settings`, icon: Settings, level: 1, permission: 'communication.settings.manage' },
      { name: 'Documentazione', href: `/${tenantSlug}/messaging/docs`, icon: HelpCircle, level: 1 },
    ]
  },
  {
    name: 'Cassa',
    href: `/${tenantSlug}/cassa`,
    icon: ShoppingCart,
    level: 0,
    moduleKey: 'pos_cassa',
    permission: 'pos.cassa',
    children: [
      { name: 'Punto Cassa', href: `/${tenantSlug}/punto-cassa`, icon: ShoppingCart, level: 1, newWindow: true },
      { name: 'Impostazioni', href: `/${tenantSlug}/cassa?tab=impostazioni-cassa`, icon: Settings2, level: 1, adminOnly: true },
    ]
  },
  { name: 'Catalogo', href: `/${tenantSlug}/catalogo`, icon: Package, level: 0, moduleKey: 'pos_catalogo', permission: 'pos.catalogo', adminAlways: true },
  {
    name: 'Amministrazione',
    href: `/${tenantSlug}/amministrazione`,
    icon: BookOpenIcon,
    level: 0,
    moduleKey: 'pos_amministrazione',
    permission: 'pos.amministrazione',
    exact: true,
  },
  {
    name: 'Magazzino',
    href: `/${tenantSlug}/amministrazione/logistica`,
    icon: Boxes,
    level: 0,
    moduleKey: 'pos_amministrazione',
    // PM-017 — stessi permessi d'area del guard BE (`/pos/inventory`,
    // `/pos/transfers`, `/pos/lookups`): chi ha permessi d'azione di
    // inventario/trasferimenti/procurement vede Magazzino anche senza
    // «POS Amministrazione».
    anyPermission: WAREHOUSE_AREA_PERMISSIONS,
  },
  // Task #3405 — hub Incentivazione (commissioning/gare). Visibile con modulo
  // attivo + permesso; adminAlways per la gestione/configurazione admin.
  { name: 'Incentivazione', href: `/${tenantSlug}/incentivazione`, icon: Trophy, level: 0, moduleKey: 'incentivazione', permission: 'incentivazione.view', adminAlways: true },
  {
    name: 'Workspace',
    icon: Send,
    level: 0,
    moduleKey: 'messenger',
    permission: 'messenger.access',
    children: [
      { name: 'Feed', href: `/${tenantSlug}/messenger/feed`, icon: Rss, level: 1 },
      { name: 'Chat', href: `/${tenantSlug}/messenger/chat`, icon: MessageSquare, level: 1 },
      { name: 'Gruppi di Lavoro', href: `/${tenantSlug}/messenger/gruppi`, icon: UsersRound, level: 1 },
      { name: 'Canali', href: `/${tenantSlug}/messenger/canali`, icon: Hash, level: 1 },
    ]
  },
  { name: 'File Manager', href: `/${tenantSlug}/files`, icon: FolderOpen, level: 0, permission: 'file_manager.access' },
  { name: 'Logs & Verbose', href: `/${tenantSlug}/logs`, icon: Terminal, level: 0, permission: 'logs.view' },
  { name: 'Integrazioni', href: `/${tenantSlug}/developer`, icon: Terminal, level: 0, permission: 'integrations.view' },
  { name: 'Workflow Builder', href: `/${tenantSlug}/workflow-builder`, icon: GitBranch, level: 0, permission: 'integrations.view' },
];

export const getFilteredNavigation = (
  items: NavItem[],
  isModuleEnabled: (key: string) => boolean,
  hasPermission: (code: string) => boolean,
  isAdmin?: boolean,
): NavItem[] => {
  return items
    .map((item) => {
      // `adminAlways` lets system tools (Company Wiki, etc.) appear for admins
      // regardless of module enable/permission gates so they can always
      // configure or troubleshoot the module.
      const adminBypass = !!(item.adminAlways && isAdmin);
      if (!adminBypass && item.moduleKey && !isModuleEnabled(item.moduleKey)) return null;
      if (!adminBypass && item.permission && !hasPermission(item.permission)) return null;
      if (!adminBypass && item.anyPermission && !item.anyPermission.some((code) => hasPermission(code))) return null;
      if (item.children) {
        const visibleChildren = item.children.filter((child) => {
          if (child.adminOnly && !isAdmin) return false;
          if (child.permission && !hasPermission(child.permission)) return false;
          return true;
        });
        if (visibleChildren.length === 0) return null;
        return { ...item, children: visibleChildren };
      }
      return item;
    })
    .filter((item): item is NavItem => item !== null);
};
